import { FC, ReactNode } from 'react'
import { GRAY_700, GRAY_800, GRAY_900 } from 'constants/styling/theme'

import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import styles from './VisualEmptyState.module.sass'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Headline, in the display face */
  title: string
  /** Body copy under the title */
  description?: string
  /** Small label over what it introduces, e.g. "Publishes to" */
  label?: string
  /** What the label introduces, between it and the chips */
  labelContent?: ReactNode
  /** A row of chips, e.g. `MUIBadge color="white" size="lg"`s */
  chips?: ReactNode
  /** Actions, e.g. `MUIButton`s */
  actions?: ReactNode
  /** The picture on the right, e.g. an `<img alt="">`; it covers a 4:3 frame */
  media: ReactNode
  /** Heading element of the title, to fit the page outline (default `h2`) */
  titleComponent?: 'h1' | 'h2' | 'h3'
  /** Below the `sm` breakpoint the actions are hidden, for a page that shows them in a `BottomBar` there */
  isActionsHiddenOnPhone?: boolean
  /** Additional class on the root, e.g. for the spacing around it */
  className?: string
}

/**
 * @component The empty state of a page that has nothing yet: a display title, a body, a labelled row of chips and the
 * actions on the left, a picture on the right. The two columns wrap on a narrow screen; on a phone the picture goes on top.
 * @example
 * <VisualEmptyState
 *   title={t('intro.title')}
 *   description={t('intro.body')}
 *   chips={<MUIBadge color="white" size="lg" label={t('intro.one_text')} />}
 *   actions={<MUIButton size="lg">{t('intro.start')}</MUIButton>}
 *   media={<img src={photoUrl} alt="" />}
 * />
 */
export const VisualEmptyState: FC<Props> = ({
  title,
  description,
  label,
  labelContent,
  chips,
  actions,
  media,
  titleComponent = 'h2',
  isActionsHiddenOnPhone = false,
  className,
}) => (
  <section className={classnames(styles.visualEmptyState, className)}>

    <div className={styles.content}>

      <Typography
        variant="display-xs"
        component={titleComponent}
        // `&&` outranks the old-style routes' global h1-h4 sizes and colour (src/styles/index.sass)
        sx={theme => ({ '&&': { margin: 0, fontSize: theme.typography['display-xs']?.fontSize, lineHeight: theme.typography['display-xs']?.lineHeight, color: GRAY_900 } })}
      >
        {title}
      </Typography>

      {!!description && <Typography variant="text-md" component="p" color={GRAY_700}>{description}</Typography>}

      {!!label && <Typography variant="text-sm" fontWeight={450} color={GRAY_800}>{label}</Typography>}

      {labelContent}

      {!!chips && <div className={styles.chips}>{chips}</div>}

      {!!actions && <div className={classnames(styles.actions, { [styles.hiddenOnPhone]: isActionsHiddenOnPhone })}>{actions}</div>}

    </div>

    <div className={styles.media}>
      <div className={styles.picture}>{media}</div>
    </div>

  </section>
)
